import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import {
  Box,
  Container,
  Paper,
} from '@mui/material'
import { setData } from './store';
import { useDispatch, useSelector } from 'react-redux';

import Space from './Space'
import About from './About'
import Notes from './Notes'
import Page from './Page'
import Reviews from './Reviews'
import Navigation from './Navigation'

const pageTheme = createTheme({
  typography: {
    fontFamily: '"Oxygen", monospace',
  },
  palette: {
    mode: 'dark',
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
          disableRipple: true,
      },
    },
    MuiLink: {
      defaultProps: {
        paddingLeft: '16px',
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        'blockquote p': {
          margin: 0,
        },
      },
    },
  },
})

const MainContent = () => {
  const location = useLocation()
  const notesMap = useSelector(state => state.data.notesMap)

  if (location.pathname === '/') {
    return <div></div>
  }

  const currentDoc = location.pathname.startsWith('/')
    ? location.pathname.slice(1)
    : location.pathname
  const currentNote = notesMap ? notesMap[currentDoc] : null
  const isPresentation = currentNote?.type === 'presentation'

  const routes = (
    <Routes>
      <Route path='/' element={<div/>} />
      <Route path='/about' element={<About/>} />
      <Route path='/notes' element={<Notes/>} />
      <Route path='/reviews' element={<Reviews/>} />
      <Route path='/:doc' element={<Page/>} />
    </Routes>
  )

  if (isPresentation) {
    return routes
  }

  return (
    <Paper sx={{ padding: '1rem', margin: '.5rem' }}>
      {routes}
    </Paper>
  )
}

const fetchText = async (url) => {
  const response = await fetch(url)
  if (!response.ok) {
    const error = new Error(`Failed to fetch ${url}: ${response.status}`)
    error.status = response.status
    throw error
  }
  return response.text()
}

const App = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviews, about, notes] = await Promise.all([
          fetchText('/REVIEWS'),
          fetchText('/ABOUT'),
          fetchText('/NOTES').then(JSON.parse),
        ]);

        const notesWithContent = await Promise.all(
          notes.map(async (note) => {
            if (note.type === 'presentation') {
              const slideTotal = Number(note.slideCount ?? note.slidesCount ?? 0)
              const length = Number.isFinite(slideTotal) && slideTotal > 0 ? Math.floor(slideTotal) : 0
              const slides = Array.from({ length }, (_, index) => ({
                src: `/slides/${note.uuid}/${index}.png`,
              }))
              return {
                ...note,
                content: '',
                slides,
              }
            }

            try {
              const content = await fetchText(`/notes/${note.uuid}`)
              return { ...note, content }
            } catch (error) {
              console.error(`Failed to load note ${note.uuid}:`, error)
              return { ...note, content: 'Unable to load note content.' }
            }
          })
        )

        const notesMap = {}
        notesWithContent.forEach((note) => {
          notesMap[note.uuid] = note
        })

        const data = {
          notes: notesWithContent,
          notesMap,
          reviews,
          about,
        }

        dispatch(setData(data))
      } catch (error) {
        console.error('Error fetching notes and about:', error)
      }
    }

    fetchData()
  }, [dispatch])

  return (
    <ThemeProvider theme={pageTheme}>
      <CssBaseline/>
      <Box
       sx={{
         position: 'fixed',
         zIndex: '-1',
         height: '100%',
         width: '100%',
         backgroundColor: 'black',
       }}>
        <Space/>
      </Box>
      <Router>
        <Navigation />
        <Container sx={{ maxWidth: 'min(100%, 680px) !important', paddingLeft: '0 !important', paddingRight: '0 !important' }}>
          <Routes>
            <Route path='*' element={<MainContent/>} />
          </Routes>
        </Container>
      </Router>
    </ThemeProvider>
  )
}

export default App
